/**
 * My progress — per-user analytics dashboard.
 *
 * Aggregates the requesting user's ticket throughput, story points, hours logged,
 * and wiki contributions over a date range. Same visual language as the admin
 * dashboard so it reads consistently.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";

import { api } from "@/lib/api";
import type { ProgressResponse } from "@/lib/types";

type Preset = "week" | "month" | "quarter" | "year" | "all";

const PRESET_LABELS: Record<Preset, string> = {
  week: "This week",
  month: "This month",
  quarter: "This quarter",
  year: "This year",
  all: "All time",
};

// Earliest plausible date — used as "all time" lower bound so the backend
// range query stays well-formed.
const ALL_TIME_START = new Date(2020, 0, 1);

function fmtISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function startOfWeek(d: Date) {
  const day = d.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + mondayOffset);
}
function endOfWeek(d: Date) {
  const s = startOfWeek(d);
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6);
}
function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function startOfQuarter(d: Date) {
  const q = Math.floor(d.getMonth() / 3);
  return new Date(d.getFullYear(), q * 3, 1);
}
function endOfQuarter(d: Date) {
  const q = Math.floor(d.getMonth() / 3);
  return new Date(d.getFullYear(), q * 3 + 3, 0);
}
function startOfYear(d: Date) {
  return new Date(d.getFullYear(), 0, 1);
}
function endOfYear(d: Date) {
  return new Date(d.getFullYear(), 11, 31);
}
function weekLabel(d: Date) {
  const s = startOfWeek(d);
  const e = endOfWeek(d);
  const sameMonth = s.getMonth() === e.getMonth();
  if (sameMonth) {
    return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${e.getDate()}, ${e.getFullYear()}`;
  }
  return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${e.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${e.getFullYear()}`;
}
function monthLabel(d: Date) {
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}
function quarterLabel(d: Date) {
  return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
}
function yearLabel(d: Date) {
  return d.getFullYear().toString();
}
function hms(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

function computeRange(preset: Preset, anchor: Date): { from: Date; to: Date; label: string } {
  if (preset === "week") {
    return { from: startOfWeek(anchor), to: endOfWeek(anchor), label: weekLabel(anchor) };
  }
  if (preset === "month") {
    return { from: startOfMonth(anchor), to: endOfMonth(anchor), label: monthLabel(anchor) };
  }
  if (preset === "quarter") {
    return { from: startOfQuarter(anchor), to: endOfQuarter(anchor), label: quarterLabel(anchor) };
  }
  if (preset === "year") {
    return { from: startOfYear(anchor), to: endOfYear(anchor), label: yearLabel(anchor) };
  }
  return { from: ALL_TIME_START, to: new Date(), label: "All time" };
}

function shiftAnchor(preset: Preset, anchor: Date, direction: 1 | -1): Date {
  const d = new Date(anchor);
  if (preset === "week") {
    d.setDate(d.getDate() + 7 * direction);
  } else if (preset === "month") {
    d.setMonth(d.getMonth() + direction, 1);
  } else if (preset === "quarter") {
    d.setMonth(d.getMonth() + 3 * direction, 1);
  } else if (preset === "year") {
    d.setFullYear(d.getFullYear() + direction, 0, 1);
  }
  return d;
}

export function MyProgressPage() {
  const [preset, setPreset] = useState<Preset>("month");
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const { from, to, label } = useMemo(() => computeRange(preset, anchor), [preset, anchor]);
  const fromISO = fmtISO(from);
  const toISO = fmtISO(to);
  const periodLabel = PRESET_LABELS[preset].toLowerCase();
  const isAllTime = preset === "all";

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["my-progress", fromISO, toISO],
    queryFn: async () =>
      (
        await api.get<ProgressResponse>(
          `/me/progress?from=${fromISO}&to=${toISO}`,
        )
      ).data,
  });

  const completionRate = useMemo(() => {
    if (!data) return 0;
    const { story_points_done, story_points_total } = data.tickets;
    return story_points_total === 0 ? 0 : story_points_done / story_points_total;
  }, [data]);

  return (
    <div>
      <header style={headerRow}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
            My progress
          </h1>
          <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
            Your tickets resolved, story points, hours logged, and wiki contributions.
          </p>
        </div>
      </header>

      <div style={toolbar}>
        <button
          type="button"
          aria-label="Previous period"
          style={{ ...navBtn, opacity: isAllTime ? 0.4 : 1, cursor: isAllTime ? "not-allowed" : "pointer" }}
          onClick={() => !isAllTime && setAnchor((d) => shiftAnchor(preset, d, -1))}
          disabled={isAllTime}
        >
          ‹
        </button>
        <span style={dateLabel}>{label}</span>
        <button
          type="button"
          aria-label="Next period"
          style={{ ...navBtn, opacity: isAllTime ? 0.4 : 1, cursor: isAllTime ? "not-allowed" : "pointer" }}
          onClick={() => !isAllTime && setAnchor((d) => shiftAnchor(preset, d, 1))}
          disabled={isAllTime}
        >
          ›
        </button>
        <button
          type="button"
          onClick={() => setAnchor(new Date())}
          style={navBtn}
          disabled={isAllTime}
        >
          Today
        </button>

        <span style={{ width: 8 }} />

        <div role="group" aria-label="Time period" style={segmentedGroup}>
          {(Object.keys(PRESET_LABELS) as Preset[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => {
                setPreset(p);
                setAnchor(new Date());
              }}
              style={segmentedBtn(preset === p)}
            >
              {PRESET_LABELS[p]}
            </button>
          ))}
        </div>

        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => refetch()}
          style={navBtn}
          disabled={isFetching}
        >
          {isFetching ? "Refreshing…" : "↻ Refresh"}
        </button>
      </div>

      {isLoading || !data ? (
        <p style={{ color: "var(--mute)" }}>Loading…</p>
      ) : (
        <>
          {/* KPI row 1 */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
              marginBottom: 12,
            }}
          >
            <KpiCard
              label="Tasks resolved"
              value={data.tickets.done.toString()}
              sub={`${data.tickets.total - data.tickets.done} open`}
              accent="var(--accent-green, #00d722)"
            />
            <KpiCard
              label="Story points done"
              value={data.tickets.story_points_done.toString()}
              sub={
                data.tickets.story_points_total > 0
                  ? `${(completionRate * 100).toFixed(0)}% of ${data.tickets.story_points_total}`
                  : "no points assigned"
              }
              accent="var(--accent-purple, #7a3dff)"
            />
            <KpiCard
              label="Hours logged"
              value={hms(data.time.logged_seconds)}
              sub={
                data.time.days_with_entries > 0
                  ? `over ${data.time.days_with_entries} day${data.time.days_with_entries === 1 ? "" : "s"}`
                  : "no entries"
              }
              accent="var(--accent-blue, #3b89ff)"
            />
            <KpiCard
              label="Docs contributions"
              value={data.wiki.revisions_made.toString()}
              sub={`${data.wiki.pages_authored} new page${data.wiki.pages_authored === 1 ? "" : "s"}`}
              accent="var(--accent-pink, #ed52cb)"
            />
          </div>

          {/* KPI row 2 — GitHub */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
              marginBottom: 32,
            }}
          >
            <KpiCard
              label="PRs raised"
              value={data.github.prs_raised.toString()}
              sub={
                data.github.connected
                  ? `${data.github.prs_merged} merged${data.github.prs_linked > 0 ? ` · ${data.github.prs_linked} linked` : ""}`
                  : "not connected"
              }
              accent="var(--accent-orange, #ff6b00)"
            />
            <KpiCard
              label="Commits"
              value={data.github.commits.toString()}
              sub={
                data.github.connected
                  ? `${data.github.commits_linked} linked to tickets`
                  : "not connected"
              }
              accent="var(--accent-orange, #ff6b00)"
            />
            <div
              style={{
                gridColumn: "span 2 / span 2",
                background: "var(--canvas)",
                border: "1px solid var(--hairline)",
                borderRadius: 8,
                padding: 16,
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              {data.github.connected ? (
                <>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        letterSpacing: 1.2,
                        textTransform: "uppercase",
                        color: "var(--mute)",
                        marginBottom: 4,
                      }}
                    >
                      GitHub
                    </div>
                    <div style={{ fontSize: 13, color: "var(--ink)" }}>
                      Connected.{" "}
                      {data.github.last_synced_at
                        ? `Last synced ${new Date(data.github.last_synced_at).toLocaleString()}.`
                        : "Run a sync to fetch your PRs and commits."}
                    </div>
                  </div>
                  <Link to="/settings" style={ghostLink}>
                    Sync settings →
                  </Link>
                </>
              ) : (
                <>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        letterSpacing: 1.2,
                        textTransform: "uppercase",
                        color: "var(--mute)",
                        marginBottom: 4,
                      }}
                    >
                      Connect GitHub
                    </div>
                    <div style={{ fontSize: 13, color: "var(--body)" }}>
                      Add a read-only PAT to auto-link your PRs and commits to tickets via branch
                      prefix.
                    </div>
                  </div>
                  <Link to="/settings" style={primaryLink}>
                    Connect →
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Status breakdown */}
          <div style={{ marginBottom: 32 }}>
            <div style={sectionTitle}>Task status (right now)</div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                gap: 12,
              }}
            >
              <StatusPill label="To do" count={data.tickets.todo} dot="var(--mute)" />
              <StatusPill label="In progress" count={data.tickets.in_progress} dot="#7a3dff" />
              <StatusPill label="In review" count={data.tickets.in_review} dot="#146ef5" />
              <StatusPill label={`Done · ${periodLabel}`} count={data.tickets.done} dot="#00d722" />
              <StatusPill label="Blocked" count={data.tickets.blocked} dot="#ee1d36" />
            </div>
          </div>

          {/* By project */}
          {data.tickets.by_project.length > 0 && (
            <section style={{ marginBottom: 32 }}>
              <div style={sectionTitle}>By project</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                  gap: 12,
                }}
              >
                {data.tickets.by_project.map((p) => {
                  const pct =
                    p.story_points_done + p.story_points_open === 0
                      ? 0
                      : p.story_points_done /
                        (p.story_points_done + p.story_points_open);
                  return (
                    <Link
                      key={p.project_id}
                      to={`/board/${p.project_key}`}
                      style={{
                        textDecoration: "none",
                        color: "inherit",
                      }}
                    >
                      <div
                        style={{
                          background: "var(--canvas)",
                          border: "1px solid var(--hairline)",
                          borderRadius: 8,
                          padding: 16,
                          transition: "border-color 120ms",
                        }}
                      >
                        <div
                          style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}
                        >
                          <span
                            aria-hidden
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 2,
                              background: p.project_color,
                            }}
                          />
                          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>
                            {p.project_name}
                          </span>
                          <span
                            style={{
                              marginLeft: "auto",
                              fontFamily: "var(--font-mono)",
                              fontSize: 11,
                              color: "var(--mute)",
                              background: "var(--surface-2)",
                              padding: "1px 6px",
                              borderRadius: 3,
                            }}
                          >
                            {p.project_key}
                          </span>
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.2px" }}>
                          {p.done}
                          <span style={{ fontSize: 13, color: "var(--mute)", fontWeight: 400 }}>
                            {" "}
                            of {p.done + p.open} done
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 4 }}>
                          {p.story_points_done} / {p.story_points_done + p.story_points_open} pts
                          · {p.in_progress} in progress
                        </div>
                        <div
                          style={{
                            marginTop: 12,
                            height: 6,
                            borderRadius: 3,
                            background: "var(--surface-2)",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: `${pct * 100}%`,
                              height: "100%",
                              background: p.project_color,
                              transition: "width 360ms",
                            }}
                          />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Recent activity */}
          <section>
            <div style={sectionTitle}>Recent activity · {periodLabel}</div>
            {data.recent_activity.length === 0 ? (
              <p style={{ color: "var(--mute)", fontSize: 13 }}>
                Nothing yet for {periodLabel}. Close a task or edit a doc to see it here.
              </p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {data.recent_activity.map((a, idx) => (
                  <li
                    key={`${a.type}-${idx}-${a.at}`}
                    style={{
                      borderTop: "1px solid var(--hairline)",
                      padding: "10px 0",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      fontSize: 13,
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 2,
                        background:
                          a.type === "issue_closed"
                            ? a.project_color ?? "#00d722"
                            : "#ed52cb",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        letterSpacing: 0.6,
                        textTransform: "uppercase",
                        color: "var(--mute)",
                        minWidth: 92,
                      }}
                    >
                      {a.type === "issue_closed" ? "closed" : "doc edit"}
                    </span>
                    {a.issue_key && (
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 11,
                          color: "var(--body-mid)",
                          background: "var(--surface-2)",
                          padding: "1px 6px",
                          borderRadius: 3,
                          flexShrink: 0,
                        }}
                      >
                        {a.issue_key}
                      </span>
                    )}
                    {a.href ? (
                      <Link
                        to={a.href}
                        style={{
                          flex: 1,
                          color: "var(--ink)",
                          textDecoration: "none",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {a.title}
                      </Link>
                    ) : (
                      <span style={{ flex: 1 }}>{a.title}</span>
                    )}
                    <time
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        color: "var(--mute)",
                        flexShrink: 0,
                      }}
                    >
                      {new Date(a.at).toLocaleDateString()}
                    </time>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub: string;
  accent: string;
}) {
  return (
    <div
      style={{
        background: "var(--canvas)",
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        padding: 16,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: accent,
          opacity: 0.85,
        }}
      />
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: "var(--mute)",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 32, fontWeight: 600, letterSpacing: "-0.4px", lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 4 }}>{sub}</div>
    </div>
  );
}

function StatusPill({ label, count, dot }: { label: string; count: number; dot: string }) {
  return (
    <div
      style={{
        background: "var(--canvas)",
        border: "1px solid var(--hairline)",
        borderRadius: 6,
        padding: "12px 14px",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span
        aria-hidden
        style={{ width: 8, height: 8, borderRadius: 999, background: dot, flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.6 }}>
          {label}
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, color: "var(--ink)" }}>{count}</div>
      </div>
    </div>
  );
}

const headerRow: CSSProperties = {
  marginBottom: 16,
  display: "flex",
  alignItems: "baseline",
  gap: 16,
};

const toolbar: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 12px",
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  marginBottom: 16,
};

const navBtn: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "5px 10px",
  fontSize: 12,
  color: "var(--ink)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const dateLabel: CSSProperties = {
  padding: "5px 14px",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  fontSize: 12.5,
  fontFamily: "var(--font-mono)",
  color: "var(--ink)",
};

const sectionTitle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 12,
};

const segmentedGroup: CSSProperties = {
  display: "inline-flex",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  overflow: "hidden",
};

function segmentedBtn(active: boolean): CSSProperties {
  return {
    background: active ? "var(--ink)" : "transparent",
    color: active ? "var(--on-primary)" : "var(--body)",
    border: "none",
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: active ? 500 : 400,
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    transition: "background 120ms",
  };
}

const ghostLink: CSSProperties = {
  fontSize: 12.5,
  color: "var(--body-mid)",
  textDecoration: "none",
  padding: "6px 12px",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  background: "var(--canvas)",
  flexShrink: 0,
};

const primaryLink: CSSProperties = {
  fontSize: 12.5,
  color: "var(--on-primary)",
  textDecoration: "none",
  padding: "6px 14px",
  background: "var(--ink)",
  borderRadius: 4,
  fontWeight: 500,
  flexShrink: 0,
};
